/**
 * space_iff.ts — friend-or-foe COLOUR for space contacts (2026-09-08: "ships
 * on your side glow red at a certain distance and green when you get closer
 * — make friendly ships always glow green at any distance").
 *
 * The distance-scaled contact markers (SpaceModule) painted EVERY combatant
 * red beyond 220 m; inside it the marker faded and the hull's own halo
 * showed — green for a wingman. Pure: one rule for the marker AND the hull
 * halo, so a friend reads green at every range.
 */
export const IFF_FRIEND = 0x5cff9d;
export const IFF_HOSTILE = 0xff5555;
export const CIV_COLOR: readonly [number, number] = [0xffd873, 0x9fe8ff]; // aurelian gold, borean ice

export interface IffInputs {
  /** A wingman spawned on the player's side. */
  ally: boolean;
  /** The contact's war side (0 aurelian, 1 borean, -1 unaligned / den pirate). */
  side: 0 | 1 | -1;
  /** The player's sworn side (-1 unaligned). */
  mySide: 0 | 1 | -1;
}

/** Is this contact on the player's side? A wingman always; a war hull only
 *  when the player has sworn to its civ. Unaligned den pirates never. */
export function isFriendly(i: IffInputs): boolean {
  return i.ally || (i.mySide !== -1 && i.side === i.mySide);
}

/** The contact-marker colour at ANY range: green friend, red everything else. */
export function contactColor(i: IffInputs): number {
  return isFriendly(i) ? IFF_FRIEND : IFF_HOSTILE;
}

/** The hull's engine-halo colour up close: green for a friend; a hostile war
 *  hull keeps its civ colour (the enemy "glows red no matter what distance"
 *  through its marker; up close its livery says which civ), a den pirate its
 *  red-orange threat glow. */
export function haloColor(i: IffInputs, civFallback: number): number {
  return isFriendly(i) ? IFF_FRIEND : civFallback;
}

/** A lane wing's marker: your own civ's wings are friends; the other civ's
 *  wear their civ colour (they turn hostile only when they PROMOTE). */
export function wingMarkerColor(wingCiv: 'aurelian' | 'borean', myFaction: string): number {
  if (myFaction === wingCiv) return IFF_FRIEND;
  return wingCiv === 'aurelian' ? CIV_COLOR[0] : CIV_COLOR[1];
}
